import { useState, useEffect } from 'react'
import { Check, ExternalLink } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import api from '../services/api'
import { T, globalStyles } from '../theme'

// Mon profil public — /dashboard/mon-profil
//
// POURQUOI CETTE PAGE EXISTE PLUTOT QU'UNE SECTION DANS LES PARAMETRES. Elle
// vivait dans CoachSettings.jsx, qui n'est servi qu'a une partie des comptes ·
// `SettingsRoute` (App.jsx) envoie les administrateurs d'un plan Club sur
// ClubSettings, ou la section n'a jamais existe. Ces comptes-la ne pouvaient
// donc pas publier leur fiche, sans que rien ne le leur dise.
//
// Le profil public n'est pas un reglage de club ni un reglage de coach · c'est
// une propriete de la PERSONNE. Il lui fallait donc une page a elle, atteignable
// sans condition de role ni de plan. C'est tout ce que ce fichier ajoute · la
// logique, elle, est celle qui tournait deja dans les parametres.

// Meme regle que le backend (app/routes/profile.py) · 3 a 80 caracteres,
// minuscules, chiffres et tirets, ni tiret en tete ni tiret en queue. La
// validation locale n'est qu'un confort · c'est le serveur qui tranche, et
// l'unicite ne peut de toute facon se verifier qu'a l'enregistrement.
const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,78}[a-z0-9]$/
const BIO_MAX = 2000
const SLUG_AIDE = '3 à 80 caractères : minuscules, chiffres et tirets. Ni tiret au début, ni à la fin.'

// Reprise du cadre de section de CoachSettings.jsx. Il y est local, donc non
// importable · la charte etant portee par `T`, le rendu est identique.
function Section({ title, accent, children }) {
  return (
    <div style={{
      background: T.surface,
      border: `1px solid ${T.ruleDark}`,
      borderTop: `2px solid ${accent || T.ruleDark}`,
      padding: '28px',
    }}>
      <div style={{
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em',
        textTransform: 'uppercase', color: T.gold,
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20,
      }}>
        <span style={{ width: 12, height: 1, background: T.gold, display: 'inline-block' }} />
        {title}
      </div>
      {children}
    </div>
  )
}

export default function MonProfilPublic() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  )

  const [charge, setCharge]   = useState(false)
  const [slug, setSlug]       = useState('')
  const [bio, setBio]         = useState('')
  const [publie, setPublie]   = useState(false)
  const [saving, setSaving]   = useState(false)
  const [message, setMessage] = useState('')   // succes
  const [erreur, setErreur]   = useState('')   // echec serveur
  const [copie, setCopie]     = useState(false)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    let vivant = true
    api.get('/profile/me/public/')
      .then(({ data }) => {
        if (!vivant) return
        setSlug(data.profile_slug || '')
        setBio(data.profile_bio || '')
        setPublie(!!data.profile_is_public)
      })
      .catch(() => {})
      .finally(() => { if (vivant) setCharge(true) })
    return () => { vivant = false }
  }, [])

  // Un slug vide est valide · il signifie « laisse le serveur en generer un a
  // partir de mon nom ». Seul un slug SAISI et mal forme bloque.
  const slugInvalide = slug !== '' && !SLUG_REGEX.test(slug)
  const lienPret = publie && slug !== '' && !slugInvalide
  const lienAffiche = `insightball.com/coach/${slug}`

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(`https://${lienAffiche}`)
      setCopie(true)
      setTimeout(() => setCopie(false), 2000)
    } catch {
      setErreur("Copie impossible — sélectionne le lien à la main.")
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage('')
    setErreur('')
    try {
      // `profile_slug` n'est envoye QUE s'il est renseigne. Envoyer une chaine
      // vide se ferait refuser par le regex serveur ; l'omettre laisse le
      // backend generer le slug depuis le nom au moment de la publication.
      const body = { profile_bio: bio, profile_is_public: publie }
      if (slug !== '') body.profile_slug = slug

      const { data } = await api.patch('/profile/me/public/', body)
      setSlug(data.profile_slug || '')
      setBio(data.profile_bio || '')
      setPublie(!!data.profile_is_public)
      setMessage('Profil mis à jour')
    } catch (e) {
      const status = e?.response?.status
      if (status === 409) setErreur('Ce lien est déjà utilisé')
      // L'intercepteur axios (lib/axios.js) a deja pose le `detail` du backend
      // dans `e.message` · c'est lui qui porte le motif exact du 400.
      else setErreur(e?.message || "L'enregistrement a échoué.")
    } finally {
      setSaving(false)
    }
  }

  const labelStyle = {
    fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em',
    textTransform: 'uppercase', color: T.muted2,
    display: 'block', marginBottom: 8,
  }
  const inputStyle = (invalide) => ({
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    background: 'transparent',
    border: `1px solid ${invalide ? 'rgba(239,68,68,0.5)' : T.ruleDark}`,
    color: T.ink, fontFamily: T.mono, fontSize: 12, outline: 'none',
  })

  return (
    <DashboardLayout>
      <style>{globalStyles}</style>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{
          fontFamily: T.mono, fontSize: 9, letterSpacing: '.2em',
          textTransform: 'uppercase', color: T.gold,
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        }}>
          <span style={{ width: 16, height: 1, background: T.gold, display: 'inline-block' }} />
          Profil public
        </div>
        <h1 style={{
          fontFamily: T.display, fontSize: isMobile ? 28 : 52,
          textTransform: 'uppercase', lineHeight: .88, letterSpacing: '.01em',
          color: T.ink, margin: 0, overflowWrap: 'break-word', wordBreak: 'break-word',
        }}>
          Mon<br /><span style={{ color: T.gold }}>profil.</span>
        </h1>
      </div>

      <div style={{ maxWidth: isMobile ? '100%' : 600 }}>
        {!charge ? (
          <Section title="Mon profil public" accent={T.ruleDark}>
            <div style={{ fontFamily: T.mono, fontSize: 11, color: T.muted2 }}>Chargement...</div>
          </Section>
        ) : (
          <Section title="Mon profil public" accent={publie ? T.gold : T.ruleDark}>
            <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted2, lineHeight: 1.7, margin: '0 0 22px 0' }}>
              Une page publique à ton nom — parcours, diplôme, identité de jeu. Partageable
              par lien, visible sans compte. Rien n'est publié tant que tu ne l'actives pas.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* ── Lien public ── */}
              <div>
                <label style={labelStyle} htmlFor="profile-slug">Mon lien</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <span style={{
                    fontFamily: T.mono, fontSize: 12, color: T.muted2,
                    padding: '10px 0 10px 12px', border: `1px solid ${T.ruleDark}`,
                    borderRight: 'none', whiteSpace: 'nowrap',
                  }}>
                    insightball.com/coach/
                  </span>
                  <input
                    id="profile-slug"
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.trim().toLowerCase())}
                    placeholder="prenom-nom"
                    maxLength={80}
                    style={{ ...inputStyle(slugInvalide), borderLeft: 'none' }}
                  />
                </div>
                {slugInvalide ? (
                  <p style={{ fontFamily: T.mono, fontSize: 10, color: 'rgba(239,68,68,0.8)', margin: '8px 0 0 0', lineHeight: 1.5 }}>
                    {SLUG_AIDE}
                  </p>
                ) : (
                  <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted2, margin: '8px 0 0 0', lineHeight: 1.5 }}>
                    {slug === ''
                      ? 'Laisse vide pour qu’il soit généré depuis ton nom à la publication.'
                      : SLUG_AIDE}
                  </p>
                )}
              </div>

              {/* ── Presentation ── */}
              <div>
                <label style={labelStyle} htmlFor="profile-bio">Présentation</label>
                <textarea
                  id="profile-bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
                  maxLength={BIO_MAX}
                  rows={6}
                  placeholder="Ton parcours, ta méthode, ce que tu cherches…"
                  style={{ ...inputStyle(false), resize: 'vertical', lineHeight: 1.7 }}
                />
                <div style={{
                  fontFamily: T.mono, fontSize: 10,
                  color: bio.length >= BIO_MAX ? 'rgba(239,68,68,0.8)' : T.muted2,
                  marginTop: 6, textAlign: 'right',
                }}>
                  {bio.length} / {BIO_MAX}
                </div>
              </div>

              {/* ── Publication ── */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 16, padding: '14px', border: `1px solid ${T.ruleDark}`,
                background: publie ? T.goldBg : 'transparent',
              }}>
                <div>
                  <div style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, marginBottom: 3 }}>
                    Rendre mon profil public
                  </div>
                  <div style={{ fontFamily: T.mono, fontSize: 10, color: T.muted2, lineHeight: 1.5 }}>
                    {publie ? 'Ta page est accessible à toute personne ayant le lien.' : 'Ta page est privée. Personne ne peut y accéder.'}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={publie}
                  aria-label="Rendre mon profil public"
                  onClick={() => setPublie(v => !v)}
                  style={{
                    flexShrink: 0, width: 44, height: 24, borderRadius: 99,
                    border: `1px solid ${publie ? T.gold : T.ruleDark}`,
                    background: publie ? T.gold : 'transparent',
                    position: 'relative', cursor: 'pointer', transition: 'all .15s',
                    padding: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 2, left: publie ? 22 : 2,
                    width: 18, height: 18, borderRadius: '50%',
                    background: publie ? '#fff' : T.muted2, transition: 'left .15s',
                  }} />
                </button>
              </div>

              {/* ── Lien a partager — seulement quand la page existe vraiment ── */}
              {lienPret && (
                <div style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
                  padding: '14px', border: `1px solid ${T.goldBdr}`, background: T.goldBg,
                }}>
                  <span style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, wordBreak: 'break-all', flex: 1 }}>
                    {lienAffiche}
                  </span>
                  {/* Le coach doit pouvoir verifier ce que voit un visiteur.
                      `noopener noreferrer` · la fiche publique est servie par
                      notre domaine, mais un onglet ouvert garde sinon une
                      poignee sur celui-ci via window.opener. */}
                  <a
                    href={`https://${lienAffiche}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 12px', border: `1px solid ${T.goldBdr}`,
                      color: T.gold, fontFamily: T.mono, fontSize: 9,
                      letterSpacing: '.1em', textTransform: 'uppercase',
                      textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <ExternalLink size={11} /> Voir
                  </a>
                  <button
                    type="button"
                    onClick={copier}
                    style={{
                      padding: '8px 14px', background: 'transparent',
                      border: `1px solid ${T.gold}`, color: T.gold,
                      fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em',
                      textTransform: 'uppercase', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {copie ? <><Check size={11} /> Copié</> : 'Copier le lien'}
                  </button>
                </div>
              )}

              {/* ── Enregistrement ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <button
                  onClick={handleSave}
                  disabled={saving || slugInvalide}
                  style={{
                    padding: '9px 20px',
                    background: slugInvalide ? T.ruleDark : T.gold,
                    border: 'none', color: '#fff',
                    fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em',
                    textTransform: 'uppercase', fontWeight: 700,
                    cursor: (saving || slugInvalide) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>

                {message && (
                  <span style={{ fontFamily: T.mono, fontSize: 11, color: T.green, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Check size={12} /> {message}
                  </span>
                )}
                {erreur && (
                  <span style={{ fontFamily: T.mono, fontSize: 11, color: 'rgba(239,68,68,0.9)' }}>
                    {erreur}
                  </span>
                )}
              </div>
            </div>
          </Section>
        )}
      </div>
    </DashboardLayout>
  )
}
